/*
 * File: OrderForm.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Form for entering order details
 */
import React, { Component } from "react";
import FormBuilder from "components/form-builder";
import FormModel from "../FormModel";
import UIProgress from "components/ui/UIProgress";
import Users from "services/users/Users";
import References from "services/references/References";
import OrderFormOptions from "../store/OrderFormOptions";
import AppStore from "Store";
import Snackbar from "components/ui/UISnackbar";
import OrderReferences from "../store/OrderReferences";
import PropTypes from "prop-types";
import FormStore from "components/form-builder/FormStore";
import OrderFormPatientField from "../components/OrderFormPatientField";
import { StoreContext } from "utils/useStore";
import { getSnapshot } from "mobx-state-tree";

class OrderForm extends Component {
  static contextType = StoreContext;

  /**
   * @var {boolean} showForm Shows form after the api calls have been made
   * @var {boolean} spinner
   * @var {array} formModel Need to rerender the view after form model changes have been made
   */
  state = {
    showForm: false,
    spinner: true,
    success: false
  };
  formModel = Object.assign([], FormModel);
  formStore = FormStore.create();

  async componentDidMount() {
    this.formStore.setFields(this.formModel);
    await this.populateFormModel();
  }

  populateIncidentOptions = async patientId => {
    if (patientId) {
      this.setState({
        spinner: true,
        showForm: false
      });
      let incidents = await this.context
        .getStore(OrderReferences)
        .getIncidents(patientId);
      this.setFormOptions(
        "incidentId",
        incidents.map(i => getSnapshot(i))
      );
      this.setState({
        spinner: false,
        showForm: true
      });
    } else {
      // remove the incident dropdown from the form since there is no patient
      let incidents = [{ id: "0", name: "None" }];
      this.setFormOptions("incidentId", incidents);
      this.setState({
        showForm: true,
        spinner: false
      });
    }
  };

  /**
   * We need to update form model options after we make api calls
   */
  async populateFormModel() {
    this.setAssignedUserId();
    const references = this.context.getStore(References);
    // fetch form data
    const statuses = await OrderFormOptions.getStatuses(this.context);
    const users = await this.context.getStore(Users).forSelect("users");
    const offices = await references.forSelect("offices");
    const providers = await this.context.getStore(Users).forSelect("providers");
    const types = await OrderFormOptions.getTypes(this.context, false);

    // update the form model options
    this.enableLongDescField(this.props.editing);
    this.populateTypeField(types);
    this.setFormOptions("orderStatusId", statuses);
    this.setFormOptions("assignedToUserId", users);
    this.setFormOptions("officeId", offices);
    this.setFormOptions("providerId", providers);
    this.populateIncidentOptions(this.props.order.patientId);
  }

  /**
   * Disables the type field if its an edit form and its a type that should be disabled.
   * Otherwise sets the list of order types
   * @param {{id: string, name: string}[]} types The list of selectable order types from the server
   */
  populateTypeField = types => {
    const order = this.props.order;
    const orderTypeField = this.formStore.getFieldByName("typeId");

    if (this.shouldDisableOrderTypeField()) {
      orderTypeField.setFormOptions([
        { id: order.typeId.toString(), name: order.typeString(this.context) }
      ]);
      orderTypeField.setDisabled(true);
    } else {
      orderTypeField.setFormOptions(types);
    }
  };

  /**
   * Checks to see if order is Careslip Order, if true, disables edit option for longdescription field.
   */
  enableLongDescField = async editing => {
    let longDescField = this.formStore.getFieldByName("longDescription");
    if (editing) {
      const orderTypeId = this.props.order.typeId;
      if (orderTypeId === 50) longDescField.setDisabled(true);
    }
  };

  /**
   *  Returns true if the order type is not allowed to be edited after its set
   */
  shouldDisableOrderTypeField = () => {
    const orderTypeId = this.props.order.typeId;
    const allowedOrderTypesInEdit = [1, 3, 4, 100, 101, 102];
    const isDisabledType = !allowedOrderTypesInEdit.includes(orderTypeId);
    return isDisabledType || !orderTypeId > 110;
  };

  /**
   * Set the default assignedToUserId on the order model based on the logged in users id.
   * This should only be auto-set for new orders
   */
  setAssignedUserId = () => {
    const { order, editing } = this.props;
    if (editing) return; //Only auto-assign for new orders
    const store = this.context.getStore(AppStore);
    const userId = store.userId;
    if (userId > 1) order.onChange("assignedToUserId", userId);
  };

  /**
   * Updates the form model options after finding the object by the key name
   *
   * @param {string} key
   * @param {string} val
   */
  setFormOptions(key, val) {
    let modelOptions = this.formStore.getFieldByName(key);
    modelOptions.setFormOptions(val);
  }

  /**
   * Handles form data changes
   *
   * @param {string} key
   * @param {string} val
   */
  onChange = (key, val) => {
    this.props.order.onChange(key, val);
  };

  /**
   * Wait for the promise to be fullfilled before we call the parentCallBack
   */
  onSubmit = async () => {
    this.setState({ spinner: true, showForm: false });

    const newOrder = await this.props.order.save();
    if (typeof this.props.parentCallBack === "function") {
      this.props.parentCallBack(newOrder.ordersId);
    }
    this.setState({ spinner: false, success: true, showForm: false });
  };

  render() {
    const { order } = this.props;
    const { spinner, showForm, success } = this.state;

    if (spinner) return <UIProgress />;
    if (!showForm) return null;
    return (
      <React.Fragment>
        <OrderFormPatientField
          populateIncidentOptions={this.populateIncidentOptions}
          order={order}
        />
        <Snackbar
          open={success}
          variant="success"
          message="Your order was successfully saved!"
        />
        <FormBuilder
          formStore={this.formStore}
          data={order}
          parentOnChange={this.onChange}
          parentOnSubmit={this.onSubmit}
        />
      </React.Fragment>
    );
  }
}

OrderForm.propTypes = {
  order: PropTypes.object.isRequired,
  parentCallBack: PropTypes.func,
  editing: PropTypes.bool
};

export default OrderForm;
